import { Copy, SmartLink } from '@stokr/components-library'
import { Section } from 'components/sto/Section'
import { useStoPageActions } from 'context/StoPageActionsContext'
import { useStoProject } from 'hooks/sto/useStoProject'
import { useStoTrack } from 'hooks/sto/useStoTracking'
import { toOnboardingLink } from 'shared/utils/app-urls'
import {
  SIGN_IN_STEP_KEY,
  STO_STEP_MODALS,
  useHowToInvest,
  type HowToInvestStep,
  type HowToInvestStepState,
} from 'hooks/sto/useEligibility'
import {
  Header,
  StepBadge,
  StepBadgeButton,
  StepDoneLabel,
  StepDescription,
  StepList,
  StepRow,
  StepText,
  StepTitle,
  StepsLabel,
  SupportLink,
} from './HowToInvest.styles'

const SUPPORT_URL = 'https://support.stokr.io/'

const BADGE_COPY = {
  start: 'stoPage.howToInvest.badge.start',
  locked: 'stoPage.howToInvest.badge.locked',
  inProgress: 'stoPage.howToInvest.badge.inProgress',
  done: 'stoPage.howToInvest.badge.done',
} as const satisfies Record<HowToInvestStepState, string>

/**
 * Sign-in and private-investor open their modal on this page; the rest link to
 * onboarding. EU professional investor's modal isn't wired yet.
 */
const StepAction = ({ step }: { step: HowToInvestStep }) => {
  const { openLoginModal, openStepModal } = useStoPageActions()
  const { slug } = useStoProject()
  const trackSto = useStoTrack()
  const label = <Copy id={BADGE_COPY[step.state]} />
  const dataCy = `sto-how-to-invest-step-${step.key}`
  const trackStep = () => trackSto('sto_how_to_invest_step_clicked', { step: step.key })

  if (step.state === 'done') {
    return <StepDoneLabel>{label}</StepDoneLabel>
  }

  if (step.state !== 'start') {
    return <StepBadge>{label}</StepBadge>
  }

  if (step.key === SIGN_IN_STEP_KEY) {
    return (
      <StepBadgeButton
        type="button"
        black
        data-cy={dataCy}
        onClick={() => {
          trackSto('sto_login_clicked', { location: 'how_to_invest' })
          openLoginModal()
        }}>
        {label}
      </StepBadgeButton>
    )
  }

  if (step.modal && STO_STEP_MODALS.includes(step.modal)) {
    const modal = step.modal
    return (
      <StepBadgeButton
        type="button"
        black
        data-cy={dataCy}
        onClick={() => {
          trackStep()
          openStepModal(modal)
        }}>
        {label}
      </StepBadgeButton>
    )
  }

  // SmartLink: onboarding URLs are in-app paths in some environments and absolute URLs
  // in others; a bare <a> would full-reload the SPA in the former case. Sign-up-flow
  // sends the visitor back to this section once the step is done.
  if (step.link) {
    return (
      <StepBadgeButton
        as={SmartLink}
        black
        {...toOnboardingLink(step.link, slug ? `/${slug}#how-to-invest` : undefined)}
        data-cy={dataCy}
        onClick={trackStep}>
        {label}
      </StepBadgeButton>
    )
  }

  return (
    <StepBadgeButton type="button" black data-cy={dataCy}>
      {label}
    </StepBadgeButton>
  )
}

/**
 * Primary-market eligibility checklist. Every row and its status comes from the ported
 * `shared/domain/projectEligibility` logic, so this page and the investor dashboard agree on what
 * a given user still has to do — which steps a project requires (self-certification,
 * private investor) is decided there, not here.
 */
export const HowToInvest = ({ mirror = false }: { mirror?: boolean }) => {
  const state = useHowToInvest()
  const trackSto = useStoTrack()

  return (
    <Section
      anchor="how-to-invest"
      title={<Copy id="stoPage.howToInvest.title" />}
      state={state}
      skeletonRows={4}
      collapsible="always"
      mirror={mirror}>
      {(steps) => (
        <div>
          <Header>
            <StepsLabel>
              <Copy id="stoPage.howToInvest.stepsLabel" />
            </StepsLabel>
            <SupportLink
              href={SUPPORT_URL}
              target="_blank"
              rel="noreferrer"
              data-cy="sto-how-to-invest-support"
              onClick={() => trackSto('sto_support_clicked', { location: 'how_to_invest', channel: 'help_center' })}>
              <Copy id="stoPage.howToInvest.questions" />{' '}
              <strong>
                <Copy id="stoPage.howToInvest.contactSupport" />
              </strong>
            </SupportLink>
          </Header>

          <StepList>
            {steps.map((step) => (
              <StepRow key={step.key} $done={step.state === 'done'}>
                <StepText>
                  <StepTitle $done={step.state === 'done'}>{step.title}</StepTitle>
                  {step.description && (
                    <StepDescription $done={step.state === 'done'}>{step.description}</StepDescription>
                  )}
                </StepText>
                <StepAction step={step} />
              </StepRow>
            ))}
          </StepList>
        </div>
      )}
    </Section>
  )
}
